/** Build-time brand accent configured via VITE_THEME_COLOR (e.g. #2563EB). */
const DEFAULT_THEME = '#2563eb';
const requested = String(import.meta.env.VITE_THEME_COLOR ?? '').trim().replace(/^['"]|['"]$/g, '');
const validHex = /^#(?:[\da-fA-F]{6}|[\da-fA-F]{3})$/.test(requested);

function normalizeHex(value: string): string {
  if (value.length === 4) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`.toLowerCase();
  }
  return value.toLowerCase();
}

function blend(first: string, second: string, weight: number): string {
  const channel = (offset: number) => {
    const a = parseInt(first.slice(offset, offset + 2), 16);
    const b = parseInt(second.slice(offset, offset + 2), 16);
    return Math.round(a * (1 - weight) + b * weight).toString(16).padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

export const themeColorConfigured = validHex;
export const themeColorRaw = requested;
export const themeColor = validHex ? normalizeHex(requested) : DEFAULT_THEME;
/** Always derive all colors from the resolved theme, even when Vite has not loaded .env yet. */
export const themePalette: [string, string, string, string, string, string, string, string, string, string] = [
  blend(themeColor, '#ffffff', .94),
  blend(themeColor, '#ffffff', .85),
  blend(themeColor, '#ffffff', .72),
  blend(themeColor, '#ffffff', .56),
  blend(themeColor, '#ffffff', .38),
  blend(themeColor, '#ffffff', .17),
  themeColor,
  blend(themeColor, '#000000', .15),
  blend(themeColor, '#000000', .32),
  blend(themeColor, '#000000', .48),
];

/** Theme shell variables, with existing CSS theme rules remaining the source of layout. */
export function applyThemeColor(): void {
  const root = document.documentElement;
  const vars: Record<string, string> = {
    '--ca-brand-canvas-light': blend(themeColor, '#ffffff', .965),
    '--ca-brand-ink-light': blend(themeColor, '#18251e', .76),
    '--ca-brand-muted-light': blend(themeColor, '#667085', .77),
    '--ca-brand-line-light': blend(themeColor, '#ffffff', .83),
    '--ca-brand-soft-light': blend(themeColor, '#ffffff', .92),
    '--ca-brand-hover-light': blend(themeColor, '#ffffff', .87),
    '--ca-brand-anchor-light': themePalette[7],
    '--ca-brand-canvas-dark': blend(themeColor, '#090e16', .12),
    '--ca-brand-panel-dark': blend(themeColor, '#121923', .16),
    '--ca-brand-line-dark': blend(themeColor, '#344054', .24),
    '--ca-brand-soft-dark': blend(themeColor, '#1c2630', .27),
    '--ca-brand-hover-dark': blend(themeColor, '#263240', .31),
    '--ca-brand-anchor-dark': themePalette[3],
    '--ca-sidebar-bg': themePalette[7],
    '--ca-sidebar-border': themePalette[8],
    '--ca-sidebar-muted': blend(themeColor, '#ffffff', .78),
    '--ca-app-accent': themeColor,
    '--ca-app-accent-strong': themePalette[7],
    '--ca-app-accent-weak': themePalette[0],
    '--ca-app-surface': blend(themeColor, '#ffffff', .96),
    '--ca-app-border': themePalette[1],
    '--ca-app-icon': blend(themeColor, '#475467', .58),
    '--ca-splash-bg': themePalette[8],
  };
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
}
